import type { Game, GameIntel, KeyPlayer, OddsLine } from './types';

const SUMMARY_URL =
  'https://site.web.api.espn.com/apis/site/v2/sports/football/nfl/summary';

const INJURY_RANK: Record<string, number> = {
  out: 0,
  doubtful: 1,
  questionable: 2,
  'injured reserve': 3,
  ir: 3,
};

const SKILL = new Set(['QB', 'RB', 'WR', 'TE', 'C', 'LT', 'RT', 'LG', 'RG', 'FB']);

interface EspnOddsBlob {
  provider?: { name?: string };
  details?: string;
  overUnder?: number;
}

interface EspnCompetitorBlob {
  id?: string;
  homeAway?: string;
  records?: Array<{ type?: string; summary?: string }>;
  team?: { id?: string };
}

interface EspnEventBlob {
  id?: string;
  weather?: { displayValue?: string; temperature?: number };
  competitions?: Array<{
    broadcasts?: Array<{ names?: string[] }>;
    odds?: EspnOddsBlob[];
    competitors?: EspnCompetitorBlob[];
    venue?: { fullName?: string; address?: { city?: string; state?: string } };
  }>;
}

interface EspnInjuryBlob {
  status?: string;
  athlete?: {
    displayName?: string;
    position?: { abbreviation?: string } | string;
  };
}

interface EspnSummaryBlob {
  article?: { headline?: string };
  predictor?: {
    homeTeam?: { id?: string; gameProjection?: string };
    awayTeam?: { id?: string; gameProjection?: string };
  };
  injuries?: Array<{
    team?: { id?: string };
    injuries?: EspnInjuryBlob[];
  }>;
  gameInfo?: {
    venue?: { fullName?: string; address?: { city?: string; state?: string } };
  };
}

function asRecord(value: unknown): string | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const recs = (value as EspnCompetitorBlob).records;
  const total = recs?.find((r) => r.type === 'total') ?? recs?.[0];
  return total?.summary;
}

function positionAbbr(value: EspnInjuryBlob['athlete']): string {
  const pos = value?.position;
  if (!pos) return '';
  if (typeof pos === 'string') return pos;
  return pos.abbreviation ?? '';
}

function playingLabel(status: string): KeyPlayer['willPlay'] {
  const s = status.toLowerCase();
  if (s.includes('out') || s.includes('injured reserve') || s === 'ir' || s.includes('doubtful')) {
    return false;
  }
  if (s.includes('questionable')) return null;
  return true;
}

function rankInjury(status: string, position: string): number {
  const s = status.toLowerCase();
  const base = Object.entries(INJURY_RANK).find(([k]) => s.includes(k))?.[1] ?? 8;
  const skillBoost = SKILL.has(position) ? 0 : 0.4;
  return base + skillBoost;
}

export function pickKeyPlayers(injuries: EspnInjuryBlob[], limit = 3): KeyPlayer[] {
  const mapped = injuries
    .map((row) => {
      const name = row.athlete?.displayName?.trim();
      const status = row.status?.trim() ?? 'Unknown';
      if (!name) return null;
      const position = positionAbbr(row.athlete);
      return {
        name,
        position,
        statusLabel: status,
        willPlay: playingLabel(status),
        rank: rankInjury(status, position),
      };
    })
    .filter((p): p is KeyPlayer & { rank: number } => p !== null)
    .sort((a, b) => a.rank - b.rank)
    .slice(0, limit);

  return mapped.map(({ name, position, statusLabel, willPlay }) => ({
    name,
    position,
    statusLabel,
    willPlay,
  }));
}

export function parseScoreboardIntel(event: EspnEventBlob): GameIntel {
  const comp = event.competitions?.[0];
  const oddsRaw = comp?.odds?.[0];
  const odds: OddsLine | undefined = oddsRaw?.details
    ? {
        provider: oddsRaw.provider?.name ?? 'DraftKings',
        details: oddsRaw.details,
        overUnder: oddsRaw.overUnder,
      }
    : undefined;

  const competitors = comp?.competitors ?? [];
  const away = competitors.find((c) => c.homeAway === 'away');
  const home = competitors.find((c) => c.homeAway === 'home');
  const weather = event.weather
    ? [event.weather.displayValue, event.weather.temperature != null ? `${event.weather.temperature}°` : '']
        .filter(Boolean)
        .join(', ')
    : undefined;
  const broadcast = comp?.broadcasts?.[0]?.names?.[0];
  const venue = comp?.venue?.fullName;

  return {
    blurb: '',
    odds,
    broadcast,
    weather,
    venue,
    records:
      away || home
        ? { away: asRecord(away) ?? '–', home: asRecord(home) ?? '–' }
        : undefined,
    awayPlayers: [],
    homePlayers: [],
  };
}

export function mergeSummaryIntel(intel: GameIntel, summary: EspnSummaryBlob, game: Game): GameIntel {
  const headline = summary.article?.headline?.trim();
  const pred = summary.predictor;
  const venue =
    intel.venue ??
    summary.gameInfo?.venue?.fullName ??
    (summary.gameInfo?.venue?.address
      ? [summary.gameInfo.venue.address.city, summary.gameInfo.venue.address.state].filter(Boolean).join(', ')
      : undefined);

  const injuryGroups = summary.injuries ?? [];
  const forTeam = (teamId: string): KeyPlayer[] => {
    const group = injuryGroups.find((g) => g.team?.id === teamId);
    return pickKeyPlayers(group?.injuries ?? []);
  };

  const awayPct = pred?.awayTeam?.gameProjection ? Number(pred.awayTeam.gameProjection) : undefined;
  const homePct = pred?.homeTeam?.gameProjection ? Number(pred.homeTeam.gameProjection) : undefined;

  return {
    ...intel,
    blurb: headline || intel.blurb,
    venue,
    predictor:
      awayPct != null && homePct != null && Number.isFinite(awayPct) && Number.isFinite(homePct)
        ? { awayPct, homePct }
        : intel.predictor,
    awayPlayers: forTeam(game.away.id),
    homePlayers: forTeam(game.home.id),
  };
}

export function isOddsStub(blurb: string): boolean {
  const text = blurb.trim();
  if (!text) return true;
  if (text.startsWith('Kickoff times')) return true;
  return text.includes(' · ') && /DraftKings|O\/U/.test(text);
}

function preferBlurb(prev?: string, next?: string): string {
  const older = prev?.trim() ?? '';
  const incoming = next?.trim() ?? '';
  if (incoming && !isOddsStub(incoming)) return incoming;
  if (older && !isOddsStub(older)) return older;
  return incoming || older;
}

export function keepRicherIntel(prev?: GameIntel, next?: GameIntel): GameIntel | undefined {
  if (!prev) return next;
  if (!next) return prev;
  return {
    ...prev,
    ...next,
    blurb: preferBlurb(prev.blurb, next.blurb),
    odds: next.odds ?? prev.odds,
    predictor: next.predictor ?? prev.predictor,
    awayPlayers: next.awayPlayers.length ? next.awayPlayers : prev.awayPlayers,
    homePlayers: next.homePlayers.length ? next.homePlayers : prev.homePlayers,
  };
}

export function mergeWeekGames(prev: Game[], next: Game[]): Game[] {
  return next.map((game) => {
    const old = prev.find((p) => p.id === game.id);
    return { ...game, intel: keepRicherIntel(old?.intel, game.intel) };
  });
}

export async function fetchGameSummary(eventId: string): Promise<EspnSummaryBlob> {
  const res = await fetch(`${SUMMARY_URL}?event=${encodeURIComponent(eventId)}`);
  if (!res.ok) throw new Error(`ESPN summary failed (${res.status})`);
  return (await res.json()) as EspnSummaryBlob;
}

export async function enrichGamesWithSummaries(games: Game[]): Promise<Game[]> {
  const next = games.map((g) => ({ ...g, intel: g.intel ? { ...g.intel } : undefined }));
  await Promise.all(
    next.map(async (game) => {
      try {
        const summary = await fetchGameSummary(game.id);
        game.intel = mergeSummaryIntel(game.intel ?? { blurb: '', awayPlayers: [], homePlayers: [] }, summary, game);
      } catch {
      }
    }),
  );
  return next;
}
